import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #isSyncingFreeGift = false;
  #isApplyingUpsellDiscount = false;
  #resolvedFreeGiftVariantId = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    this.#syncFreeGiftWithCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleCartUpdate = () => {
    this.#syncFreeGiftWithCart();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  async #syncFreeGiftWithCart() {
    if (this.#isSyncingFreeGift) return;

    const thresholdRs = Number(this.dataset.freeGiftThresholdRs || 0);
    const discountThresholdRs = Number(this.dataset.upsellDiscountThresholdRs || 0);
    const upsellDiscountCode = (this.dataset.upsellDiscountCode || '').trim();
    const freeGiftVariantId = await this.#resolveFreeGiftVariantId();

    if (!freeGiftVariantId || !thresholdRs) return;

    this.#isSyncingFreeGift = true;

    try {
      let cart = await this.#fetchCart();
      if (!cart || !Array.isArray(cart.items)) return;

      let didMutateCart = false;
      const discountThresholdPaise = Math.round(discountThresholdRs * 100);
      const initialCartBaseTotal = cart.original_total_price ?? cart.total_price ?? 0;
      const isDiscountEligible = Boolean(upsellDiscountCode) && initialCartBaseTotal >= discountThresholdPaise;
      const isDiscountApplied = this.#isDiscountAlreadyApplied(cart, upsellDiscountCode);

      // Step 1: apply discount first (if needed), then continue to gift logic in same cycle.
      if (isDiscountEligible && !isDiscountApplied && !this.#isApplyingUpsellDiscount) {
        this.#isApplyingUpsellDiscount = true;
        await this.#applyDiscountCode(upsellDiscountCode);
        didMutateCart = true;

        const updatedCartAfterDiscount = await this.#fetchCart();
        if (updatedCartAfterDiscount && Array.isArray(updatedCartAfterDiscount.items)) {
          cart = updatedCartAfterDiscount;
        }
      }

      // Step 2: gift sync based on latest cart state.
      const cartBaseTotal = cart.original_total_price ?? cart.total_price ?? 0;
      const thresholdPaise = Math.round(thresholdRs * 100);
      const isEligible = cartBaseTotal >= thresholdPaise;
      const freeGiftItems = cart.items.filter((item) => {
        if (String(item.variant_id) !== String(freeGiftVariantId)) return false;

        const freeGiftFlag = item.properties?._free_gift;
        return String(freeGiftFlag || '').toLowerCase() === 'true';
      });

      if (isEligible && freeGiftItems.length === 0) {
        await this.#addFreeGift(freeGiftVariantId);
        didMutateCart = true;
      }

      if (!isEligible && freeGiftItems.length > 0) {
        await this.#removeFreeGift(freeGiftItems);
        didMutateCart = true;
      }

      if (didMutateCart) {
        await this.#emitCartUpdate();
      }
    } catch (error) {
      console.error('Failed to sync free gift in cart drawer:', error);
    } finally {
      this.#isSyncingFreeGift = false;
      this.#isApplyingUpsellDiscount = false;
    }
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    if (!response.ok) return null;
    return response.json();
  }

  async #addFreeGift(variantId) {
    const addUrl = this.#toAjaxEndpoint(Theme.routes.cart_add_url, '/cart/add.js');
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              _free_gift: 'True',
            },
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error('Unable to add free gift');
    }
  }

  async #removeFreeGift(freeGiftItems) {
    const changeUrl = this.#toAjaxEndpoint(Theme.routes.cart_change_url, '/cart/change.js');

    for (const item of freeGiftItems) {
      const response = await fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: item.key,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        throw new Error('Unable to remove free gift');
      }
    }
  }

  #isDiscountAlreadyApplied(cart, discountCode) {
    if (!discountCode) return false;

    const normalizedCode = discountCode.toLowerCase();
    const discountCodes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
    const hasCodeFromArray = discountCodes.some((entry) => {
      if (typeof entry === 'string') return entry.toLowerCase() === normalizedCode;
      if (entry && typeof entry.code === 'string') return entry.code.toLowerCase() === normalizedCode;
      return false;
    });

    if (hasCodeFromArray) return true;

    const cartLevelDiscounts = Array.isArray(cart.cart_level_discount_applications)
      ? cart.cart_level_discount_applications
      : [];

    return cartLevelDiscounts.some((discount) => {
      const title = (discount?.title || '').toString().toLowerCase();
      return title.includes(normalizedCode);
    });
  }

  async #applyDiscountCode(discountCode) {
    const response = await fetch(`/discount/${encodeURIComponent(discountCode)}?redirect=/cart`, {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error('Unable to auto-apply upsell discount code');
    }
  }

  async #emitCartUpdate() {
    const latestCart = await this.#fetchCart();
    const itemCount = latestCart?.item_count || 0;

    this.dispatchEvent(
      new CartUpdateEvent({}, this.id, {
        source: 'cart-drawer-free-gift',
        itemCount,
      })
    );
  }

  #toAjaxEndpoint(routeValue, fallback) {
    if (typeof routeValue !== 'string' || !routeValue) return fallback;
    return routeValue.endsWith('.js') ? routeValue : `${routeValue}.js`;
  }

  async #resolveFreeGiftVariantId() {
    if (this.#resolvedFreeGiftVariantId) return this.#resolvedFreeGiftVariantId;

    const directVariantId = (this.dataset.freeGiftVariantId || '').trim();
    if (directVariantId) {
      this.#resolvedFreeGiftVariantId = directVariantId;
      return this.#resolvedFreeGiftVariantId;
    }

    const handle = (this.dataset.freeGiftProductHandle || '').trim();
    if (!handle) return null;

    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) return null;

      const product = await response.json();
      const firstVariantId = product?.variants?.[0]?.id;
      if (!firstVariantId) return null;

      this.#resolvedFreeGiftVariantId = String(firstVariantId);
      return this.#resolvedFreeGiftVariantId;
    } catch (_) {
      return null;
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
